/**
 * Dashboard Page - Punto de entrada principal a la plataforma
 *
 * Flujo: Dashboard → elegir/crear guía → abrir editor existente
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Box } from "@mui/material";
import { useFirebase } from "../context/ApiContext";
import {
	DashboardLayout,
	WelcomeSection,
	ExploreSection,
	MyGuidesSection,
	PacksSection,
	GuideDoorsSection,
} from "../components/Dashboard";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import { PurchaseChoiceModal } from "../components/PurchaseChoiceModal";
import ComingSoonModal from "../components/ComingSoonModal";
import EmailVerificationModal from "../components/EmailVerificationModal";
import { extractCoverFromPages } from "../utils/guideCover";
import { getApiOrigin } from "../utils/api";
import { logger } from "../hooks/Logger";
import { trackGuideInteraction, trackPurchaseAction } from "../utils/analytics";
import SeoHead from "../components/Seo/SeoHead";
import { GUIDE_LANDINGS } from "../data/guideLandings";
import { homeJsonLd } from "../utils/seoSchema";
import { getSiteOrigin } from "../config/site";

// Import Dashboard styles
import "../components/Dashboard/Dashboard.css";

import { GUIDE_CATALOG_IMAGES, sortGuideCatalog } from "../data/guideCatalogImages";
import plantillaTodo from "../assets/to-do.png";
import travelkitCover from "../assets/travel-kit2.png";

// Tamaño de página para "Mis guías" (debe ser el mismo en carga inicial y en "load more")
const MY_GUIDES_PAGE_SIZE = 12;

// Nivel viajero (para stats del dashboard — logros viajeros)
const TRAVELER_LEVEL_LABELS = [
	"Viajero",
	"Explorador",
	"Gran explorador",
	"Viajero experto",
	"Globetrotter",
	"Ciudadano del mundo",
];
const getTravelerLevelLabel = (count) => {
	if (count < 5) return TRAVELER_LEVEL_LABELS[0];
	if (count < 15) return TRAVELER_LEVEL_LABELS[1];
	if (count < 30) return TRAVELER_LEVEL_LABELS[2];
	if (count < 50) return TRAVELER_LEVEL_LABELS[3];
	if (count < 80) return TRAVELER_LEVEL_LABELS[4];
	return TRAVELER_LEVEL_LABELS[5];
};

// Mismo sistema para todas: imports → el bundler sirve en /static/media/...
const imageMap = {
	...GUIDE_CATALOG_IMAGES,
	"todo-viaje": plantillaTodo,
	travelkit: travelkitCover,
};

const DashboardPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		user,
		subscription,
		initialLoading,
		handleLogout: contextLogout,
		importPrebuiltGuide,
		getPrebuiltGuides,
		readProjects,
		readProjectsPaginated,
		readPages,
		deleteProject,
		createSharedGuide,
		getSharedGuides,
		createProject,
		createPage,
		checkGuideAccess,
		purchaseGuide,
		purchaseSubscription,
		canCreateGuide,
		favorites,
		toggleFavorite,
		getPassport,
		getTravelCard,
		isBlockedByEmailVerification,
		refreshSubscription,
		// Packs
		getPacks,
		purchasePack,
	} = useFirebase();

	const [activeSection, setActiveSection] = useState("home");

	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("login");
	const [justInitSession, setJustInitSession] = useState(false);
	const [purchasingGuide, setPurchasingGuide] = useState(null);
	const [purchasingPackId, setPurchasingPackId] = useState(null);

	// Lanzar la compra de un pack desde la sección Packs.
	// Si el user no está logueado, abrimos auth en modo signup.
	const handlePurchasePack = async (pack) => {
		if (!user) {
			setAuthMode("signup");
			setShowLogin(true);
			return;
		}
		setPurchasingPackId(pack.id);
		try {
			await purchasePack(pack.id);
		} catch (err) {
			enqueueSnackbar(err?.message || "No se pudo iniciar la compra del pack", {
				variant: "error",
			});
			setPurchasingPackId(null);
		}
	};
	const [purchaseChoiceModal, setPurchaseChoiceModal] = useState({
		open: false,
		guide: null,
		limitInfo: null,
	});
	const [comingSoonModal, setComingSoonModal] = useState({
		open: false,
		guideName: null,
	});
	const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);

	// Data state
	const [featuredGuides, setFeaturedGuides] = useState([]);
	const [myGuides, setMyGuides] = useState([]);
	const [loadingGuides, setLoadingGuides] = useState(true);
	const [loadingMoreGuides, setLoadingMoreGuides] = useState(false);
	const [guidesPage, setGuidesPage] = useState(1);
	const [hasMoreGuides, setHasMoreGuides] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");

	// Perfil viajero: datos para el sidebar
	const [countriesCount, setCountriesCount] = useState(0);
	const [travelCard, setTravelCard] = useState(null);

	// Imagen de portada por guía (primera página) para "Mis guías"
	const [guideCoverImages, setGuideCoverImages] = useState({});

	// Cargar datos del perfil viajero (pasaporte + tarjeta desde backend)
	useEffect(() => {
		const loadTravelerProfile = async () => {
			if (!user) {
				setCountriesCount(0);
				setTravelCard(null);
				return;
			}
			try {
				const passport = await getPassport();
				setCountriesCount(passport?.countries?.length || 0);
			} catch (_) {}
			try {
				const card = await getTravelCard();
				setTravelCard(card || null);
			} catch (_) {}
		};
		loadTravelerProfile();
	}, [user, getPassport, getTravelCard, location.pathname]);

	// Cargar guías
	useEffect(() => {
		const loadGuides = async () => {
			setLoadingGuides(true);
			try {
				// Cargar guías prebuilt
				const prebuiltGuides = await getPrebuiltGuides();

				// Mapear con imágenes locales y info de acceso (mostrar todas las guías del API)
				const guidesWithImages = (prebuiltGuides || []).map((guide) => {
					const access = checkGuideAccess(guide.id, guide);
					return {
						...guide,
						coverImage: imageMap[guide.id] || guide.coverImage,
						days: guide.days || null,
						tags: guide.tags || [],
						isFree: guide.isFree,
						price: guide.price,
						userHasAccess: guide.userHasAccess ?? access.hasFullAccess,
						accessReason: guide.accessReason ?? access.reason,
					};
				});

				setFeaturedGuides(sortGuideCatalog(guidesWithImages));

				// Cargar proyectos del usuario (primera página, paginado)
				if (user) {
					const { projects, hasMore } = await readProjectsPaginated({
						page: 1,
						limit: MY_GUIDES_PAGE_SIZE,
					});
					setMyGuides(projects || []);
					setHasMoreGuides(!!hasMore);
					setGuidesPage(1);
				} else {
					setMyGuides([]);
					setGuideCoverImages({});
					setHasMoreGuides(false);
					setGuidesPage(1);
				}
			} catch (error) {
				logger.error("Dashboard: error cargando guías", {
					message: error?.message,
				});
				// Datos de fallback
				setFeaturedGuides([
					{
						id: "kioto",
						name: "4 días en Kioto",
						coverImage: imageMap.kioto,
						days: 4,
						description:
							"Descubre los templos, santuarios y barrios históricos de Kioto",
						tags: ["Asia", "Japón"],
					},
					{
						id: "tokio",
						name: "6 días en Tokio",
						coverImage: imageMap.tokio,
						days: 6,
						description:
							"Explora la capital de Japón: tecnología, cultura y gastronomía",
						tags: ["Asia", "Japón"],
					},
					{
						id: "ny",
						name: "7 días en Nueva York",
						coverImage: imageMap.ny,
						days: 7,
						description:
							"La ciudad que nunca duerme: rascacielos, cultura y vida urbana",
						tags: ["América", "USA"],
					},
					{
						id: "londres",
						name: "4 días en Londres",
						coverImage: imageMap.londres,
						days: 4,
						description: "Museos, teatros, pubs y la realeza británica",
						tags: ["Europa", "UK"],
					},
					{
						id: "seul",
						name: "5 días en Seúl",
						coverImage: imageMap.seul,
						days: 5,
						description:
							"Palacios, barrios tradicionales, K-pop y la modernidad coreana",
						tags: ["Asia", "Corea del Sur"],
					},
					{
						id: "sanFrancisco",
						name: "3 días en San Francisco",
						coverImage: imageMap.sanFrancisco,
						days: 3,
						description:
							"El puente Golden Gate, Cable Cars y los barrios con personalidad",
						tags: ["USA", "California"],
					},
				]);
			} finally {
				setLoadingGuides(false);
			}
		};

		loadGuides();
		// Solo recargar cuando cambie usuario o suscripción; no cuando cambien favoritos u otras partes del contexto
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, subscription]);

	// Abrir modal de compra si se llega desde la página CTA del Viewer (vista previa limitada)
	useEffect(() => {
		const guideId = location.state?.openPurchaseForGuideId;
		if (!guideId || !featuredGuides.length) return;
		const guide = featuredGuides.find((g) => g.id === guideId);
		if (guide) {
			setPurchaseChoiceModal({ open: true, guide });
			window.history.replaceState({}, document.title, location.pathname);
		}
	}, [
		location.state?.openPurchaseForGuideId,
		location.pathname,
		featuredGuides,
	]);

	// Fallback: si el proyecto no tiene coverImage guardado (screenshot),
	// extraer una imagen de las páginas como portada provisional
	useEffect(() => {
		if (!user || !myGuides.length || !readPages) return;
		const apiOrigin = getApiOrigin();
		myGuides.forEach((guide) => {
			// Si ya tiene screenshot del backend o ya tenemos cover cacheado, saltar
			if (guide.coverImage || guideCoverImages[guide.id]) return;
			readPages(guide.id)
				.then((pages) => {
					if (!pages || pages.length === 0) return;
					const url = extractCoverFromPages(pages, apiOrigin);
					if (url)
						setGuideCoverImages((prev) => ({ ...prev, [guide.id]: url }));
				})
				.catch(() => {});
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, myGuides, readPages]);

	// Formatear usuario
	const formattedUser = user
		? {
				id: user.uid || user.id,
				displayName:
					user.displayName ||
					user.name ||
					user.email?.split("@")[0] ||
					"Usuario",
				email: user.email,
			}
		: null;

	// === Handlers ===

	const handleSectionChange = (sectionId) => {
		if (sectionId === "explore") {
			navigate("/explorar");
			return;
		}
		if (sectionId === "my-guides") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus guías", {
					variant: "info",
				});
				return;
			}
			navigate("/mis-guias");
			return;
		}
		if (sectionId === "favorites") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus favoritos", {
					variant: "info",
				});
				return;
			}
			navigate("/favoritos");
			return;
		}
		if (sectionId === "passport") {
			navigate("/passport");
			return;
		}
		if (sectionId === "travel-card") {
			navigate("/tarjeta-viajera");
			return;
		}
		// logros-viajeros comentado
		// if (sectionId === "logros-viajeros") {
		// 	if (!user) {
		// 		setAuthMode("login");
		// 		setShowLogin(true);
		// 		enqueueSnackbar("Inicia sesión para ver tus logros viajeros", {
		// 			variant: "info",
		// 		});
		// 		return;
		// 	}
		// 	navigate("/logros-viajeros");
		// 	return;
		// }
		if (sectionId === "discounts") {
			navigate("/descuentos");
			return;
		}
		if (sectionId === "rutas") {
			navigate("/rutas");
			return;
		}
		if (sectionId === "crear-ruta") {
			navigate("/rutas");
			return;
		}
		if (sectionId === "modules") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus módulos", {
					variant: "info",
				});
				return;
			}
			navigate("/modulos");
			return;
		}
		setActiveSection(sectionId);

		if (sectionId === "create") {
			handleCreateFromScratch();
		}
	};

	const handleViewGuide = (guideId) => {
		trackGuideInteraction("preview_clicked", guideId, { source: "dashboard" });
		navigate(`/preview/prebuilt/${guideId}`, {
			state: { from: `${location.pathname}${location.search}` },
		});
	};

	const handleCustomizeGuide = async (guideId) => {
		if (!user) {
			setAuthMode("login");
			setShowLogin(true);
			enqueueSnackbar("Inicia sesión para personalizar una guía", {
				variant: "info",
			});
			return;
		}
		trackGuideInteraction("customize_clicked", guideId, {
			source: "dashboard",
		});

		const guide = featuredGuides.find((g) => g.id === guideId);
		if (guide && !guide.isFree && !guide.userHasAccess) {
			enqueueSnackbar(
				`Esta guía requiere compra o suscripción (${
					guide.price ? `€${guide.price}` : "Premium"
				})`,
				{ variant: "warning" },
			);
			return;
		}

		// Si el usuario tiene acceso a esta guía por pack/compra, no aplicar
		// el límite de guías del plan free — el pack te da derecho a editarla.
		const hasAccessByEntitlement =
			guide?.userHasAccess || guide?.accessReason === "PACK";
		const createCheck = canCreateGuide();
		if (!createCheck.allowed && !hasAccessByEntitlement) {
			setPurchaseChoiceModal({
				open: true,
				guide,
				limitInfo: {
					currentGuides: createCheck.currentGuides,
					maxGuides: createCheck.maxGuides,
				},
			});
			return;
		}

		try {
			enqueueSnackbar("Abriendo guía...", { variant: "info" });
			const result = await importPrebuiltGuide(guideId);

			if (result && result.project) {
				enqueueSnackbar(
					result.alreadyExists
						? "Abriendo tu guía"
						: "Guía importada correctamente",
					{ variant: "success" },
				);
				const userId = result.project.userId || user.uid || user.id;
				const projectId = result.project.id;
				navigate(`/usuarios/${userId}/guias/${projectId}`);
			} else {
				throw new Error("No se pudo crear la guía");
			}
		} catch (error) {
			console.error("Error importing guide:", error);
			if (
				error.code === "GUIDE_LIMIT" ||
				error.message?.includes("límite de guías")
			) {
				setPurchaseChoiceModal({
					open: true,
					guide,
					limitInfo: {
						currentGuides: createCheck.currentGuides,
						maxGuides: createCheck.maxGuides,
					},
				});
			} else if (
				error.code === "ACCESS_DENIED" ||
				error.message?.includes("acceso")
			) {
				enqueueSnackbar(
					"No tienes acceso a esta guía. Cómprala o suscríbete.",
					{
						variant: "warning",
					},
				);
			} else if (
				error.response?.data?.error === "EMAIL_NOT_VERIFIED" ||
				error.message?.includes("verificar tu email")
			) {
				setShowEmailVerifyModal(true);
			} else {
				enqueueSnackbar(error.message || "No se pudo crear la guía", {
					variant: "error",
				});
			}
		}
	};

	const handlePurchaseClick = (guide) => {
		if (!user) {
			setAuthMode("login");
			setShowLogin(true);
			enqueueSnackbar("Inicia sesión para comprar esta guía", {
				variant: "info",
			});
			return;
		}
		if (isBlockedByEmailVerification("payment")) {
			setShowEmailVerifyModal(true);
			return;
		}
		trackPurchaseAction("modal_opened", {
			guide_id: guide?.id ? String(guide.id) : undefined,
			guide_name: guide?.name,
			source: "dashboard",
		});
		setPurchaseChoiceModal({ open: true, guide });
	};

	const handleConfirmBuyGuide = async (guide) => {
		if (!guide?.id) return;
		try {
			await purchaseGuide(guide.id);
		} catch (error) {
			logger.error("Dashboard: error comprando guía", {
				guideId: guide?.id,
				message: error?.message,
			});
			enqueueSnackbar(error.message || "Error al procesar la compra", {
				variant: "error",
			});
			setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
			throw error;
		}
	};

	const handleConfirmSubscribe = async () => {
		try {
			await purchaseSubscription();
		} catch (error) {
			logger.error("Dashboard: error suscripción", {
				message: error?.message,
			});
			enqueueSnackbar(error.message || "Error al iniciar la suscripción", {
				variant: "error",
			});
			setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
			throw error;
		}
	};

	const handleCreateFromScratch = async () => {
		if (!user) {
			navigate("/crear-guia");
			return;
		}
		const createCheck = canCreateGuide();
		if (!createCheck.allowed) {
			setPurchaseChoiceModal({
				open: true,
				guide: { id: "new", name: "Nueva guía" },
				limitInfo: {
					currentGuides: createCheck.currentGuides,
					maxGuides: createCheck.maxGuides,
				},
			});
			return;
		}
		navigate("/crear-guia");
	};

	const handleOpenMyGuide = async (guideId) => {
		if (!user) {
			setAuthMode("login");
			setShowLogin(true);
			return;
		}
		trackGuideInteraction("opened", guideId, { source: "dashboard" });

		try {
			const project = myGuides.find((p) => p.id === guideId);
			if (project && project.userId) {
				navigate(`/usuarios/${project.userId}/guias/${guideId}`);
			} else {
				const userId = user.uid || user.id;
				navigate(`/usuarios/${userId}/guias/${guideId}`);
			}
		} catch (error) {
			console.error("Error opening guide:", error);
			enqueueSnackbar("Error al abrir la guía", { variant: "error" });
		}
	};

	const handleExportMyGuide = (guideId) => {
		enqueueSnackbar("Función de exportación próximamente", { variant: "info" });
	};

	const handleDeleteGuide = async (guideId) => {
		trackGuideInteraction("deleted", guideId, { source: "dashboard" });
		try {
			await deleteProject(guideId);
			// Recargar primera página de guías y límites para que el sistema tenga el conteo actualizado
			const { projects, hasMore } = await readProjectsPaginated({
				page: 1,
				limit: MY_GUIDES_PAGE_SIZE,
			});
			setMyGuides(projects || []);
			setHasMoreGuides(!!hasMore);
			setGuidesPage(1);
			await refreshSubscription();
			enqueueSnackbar("Guía eliminada", { variant: "success" });
		} catch (error) {
			console.error("Error deleting guide:", error);
			enqueueSnackbar("Error al eliminar la guía", { variant: "error" });
		}
	};

	const handleShareGuide = async (guideId) => {
		trackGuideInteraction("share_clicked", guideId, { source: "dashboard" });
		try {
			const list = await getSharedGuides();
			const publicLink = Array.isArray(list)
				? list.find((s) => s.projectId === guideId && s.isPublic === true)
				: null;
			const url = publicLink?.url;
			if (url) {
				navigator.clipboard.writeText(url);
				enqueueSnackbar("Enlace copiado al portapapeles", {
					variant: "success",
				});
				return;
			}
			const result = await createSharedGuide(guideId, true);
			if (
				result?.error === "TEMPLATE_CANNOT_BE_SHARED_PUBLICLY" ||
				result?.error === "OFFICIAL_TEMPLATE_CANNOT_BE_SHARED_PUBLICLY"
			) {
				enqueueSnackbar(
					"Las guías basadas en plantillas no se pueden compartir públicamente.",
					{
						variant: "warning",
					},
				);
				return;
			}
			const newUrl = result?.shareUrl ?? result?.url;
			if (newUrl) {
				navigator.clipboard.writeText(newUrl);
				enqueueSnackbar("Enlace copiado al portapapeles", {
					variant: "success",
				});
			} else {
				enqueueSnackbar("Guía compartida", { variant: "success" });
			}
		} catch (error) {
			console.error("Error sharing guide:", error);
			enqueueSnackbar("Error al compartir la guía", { variant: "error" });
		}
	};

	const handleViewMyGuide = async (guideId) => {
		if (!user) {
			setAuthMode("login");
			setShowLogin(true);
			return;
		}
		try {
			const list = await getSharedGuides();
			const privateLink = Array.isArray(list)
				? list.find((s) => s.projectId === guideId && s.isPublic === false)
				: null;
			const viewUrl = privateLink?.url;
			if (viewUrl) {
				const path = viewUrl.startsWith("/")
					? viewUrl
					: new URL(viewUrl).pathname;
				navigate(path);
				return;
			}
			const result = await createSharedGuide(guideId, false);
			const newUrl = result?.shareUrl ?? result?.url;
			if (newUrl) {
				const path = newUrl.startsWith("/") ? newUrl : new URL(newUrl).pathname;
				navigate(path);
			} else if (result?.token ?? result?.shareToken) {
				navigate(`/view/${result.token ?? result.shareToken}`);
			} else {
				enqueueSnackbar("No se pudo abrir la vista previa", {
					variant: "warning",
				});
			}
		} catch (error) {
			console.error("Error opening preview:", error);
			enqueueSnackbar("Error al abrir la vista previa", { variant: "error" });
		}
	};

	const handleLogin = () => {
		setAuthMode("login");
		setShowLogin(true);
	};

	const handleSignUp = () => {
		setAuthMode("signup");
		setShowLogin(true);
	};

	const handleLoadMoreMyGuides = async () => {
		if (!user || loadingMoreGuides || !hasMoreGuides) return;
		setLoadingMoreGuides(true);
		try {
			const nextPage = guidesPage + 1;
			const { projects, hasMore } = await readProjectsPaginated({
				page: nextPage,
				limit: MY_GUIDES_PAGE_SIZE,
			});
			setMyGuides((prev) => [...prev, ...(projects || [])]);
			setHasMoreGuides(!!hasMore);
			setGuidesPage(nextPage);
		} catch (e) {
			enqueueSnackbar("Error al cargar más guías", { variant: "error" });
		} finally {
			setLoadingMoreGuides(false);
		}
	};

	const handleLogout = async () => {
		await contextLogout();
		setMyGuides([]);
		setHasMoreGuides(false);
		setGuidesPage(1);
		enqueueSnackbar("Sesión cerrada", { variant: "success" });
	};

	const handleSearch = (query) => {
		setSearchQuery(query);
	};

	const handleToggleFavorite = async (guideId) => {
		try {
			await toggleFavorite(guideId);
			enqueueSnackbar(
				favorites?.includes(guideId)
					? "Quitado de favoritos"
					: "Añadido a favoritos",
				{ variant: "success" },
			);
		} catch (error) {
			enqueueSnackbar("Error al actualizar favoritos", { variant: "error" });
		}
	};

	// Filtrar guías por búsqueda y excluir las que solo se venden como pack
	const filteredFeaturedGuides = featuredGuides
		.filter((guide) => !guide.requiresPack)
		.filter(
			(guide) =>
				!searchQuery ||
				guide.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
				guide.description?.toLowerCase().includes(searchQuery.toLowerCase()),
		);

	const filteredMyGuides = searchQuery
		? myGuides.filter((guide) =>
				guide.name?.toLowerCase().includes(searchQuery.toLowerCase()),
			)
		: myGuides;

	return (
		<>
			<SeoHead
				title="Guías de viaje interactivas"
				description="Guías de viaje digitales para explorar destinos, preparar tu itinerario y llevar el plan en el móvil: plantillas por ciudad, mapas, consejos prácticos y pasaporte viajero. Viajes Molones."
				path="/"
				jsonLd={homeJsonLd(getSiteOrigin(), GUIDE_LANDINGS)}
			/>
			{initialLoading ? (
				<></>
			) : (
				<>
					<DashboardLayout
						user={formattedUser}
						activeSection={activeSection}
						onSectionChange={handleSectionChange}
						onLogin={handleLogin}
						onSignUp={handleSignUp}
						onLogout={handleLogout}
						onCreateNew={handleCreateFromScratch}
						onSearch={handleSearch}
						prebuiltGuides={featuredGuides}
						myGuides={myGuides}
						imageMap={imageMap}
						travelCard={travelCard}
						countriesCount={countriesCount}
						isDashboardHome
						onComingSoonGuide={(g) =>
							setComingSoonModal({ open: true, guideName: g?.name ?? null })
						}
					>
						<>
							{/* Welcome / Hero Section */}
							<WelcomeSection
								user={formattedUser}
								onCreateFromScratch={handleCreateFromScratch}
								onExploreGuides={() => handleSectionChange("explore")}
								onCreateRoute={() => navigate("/rutas")}
								onGoToPassport={() => handleSectionChange("passport")}
								onGoToTravelCard={() => handleSectionChange("travel-card")}
								onGoToMyGuides={() => handleSectionChange("my-guides")}
								guidesCount={myGuides.length}
								loading={loadingGuides}
								countriesCount={countriesCount}
								hasTravelCard={!!travelCard}
								travelerLevelLabel={getTravelerLevelLabel(countriesCount)}
							/>

							{/* Secciones con padding horizontal en móvil (el layout tiene px:0 para el hero) */}
							<Box sx={{ px: { xs: 2, sm: 0 } }}>
								<GuideDoorsSection
									onGuides={() => handleSectionChange("explore")}
									onCreate={handleCreateFromScratch}
								/>

								{/* Explore Guides Section */}
								<div id="explore-section">
									<ExploreSection
										guides={filteredFeaturedGuides}
										loading={loadingGuides}
										onViewGuide={handleViewGuide}
										onCustomizeGuide={handleCustomizeGuide}
										onPurchaseGuide={handlePurchaseClick}
										onComingSoonClick={(g) =>
											setComingSoonModal({
												open: true,
												guideName: g?.name ?? null,
											})
										}
										purchasingGuideId={purchasingGuide}
										onSeeAll={() => handleSectionChange("explore")}
										user={formattedUser}
										onLogin={() => {
											setAuthMode("signup");
											setShowLogin(true);
										}}
										favoriteIds={favorites || []}
										onToggleFavorite={user ? handleToggleFavorite : null}
									/>
								</div>

								{/* Packs destacados (Pack Japón, etc.) */}
								<PacksSection
									getPacks={getPacks}
									onPurchasePack={handlePurchasePack}
									onLogin={() => {
										setAuthMode("signup");
										setShowLogin(true);
									}}
									isLoggedIn={!!user}
									purchasingPackId={purchasingPackId}
									onOpenGuide={handleViewGuide}
									onEditGuide={handleCustomizeGuide}
								/>

								{/* My Guides Section (solo si está logueado) */}
								{user && (
									<div id="my-guides-section">
										<MyGuidesSection
											guides={filteredMyGuides.map((g) => ({
												...g,
												coverImage: g.coverImage || guideCoverImages[g.id],
											}))}
											loading={loadingGuides}
											loadingMore={loadingMoreGuides}
											hasMore={hasMoreGuides}
											onLoadMore={handleLoadMoreMyGuides}
											onOpenGuide={handleOpenMyGuide}
											onViewGuide={handleViewMyGuide}
											onDeleteGuide={handleDeleteGuide}
											onCreateNew={handleCreateFromScratch}
											onSeeAll={() => handleSectionChange("my-guides")}
										/>
									</div>
								)}
							</Box>
						</>
					</DashboardLayout>

					{/* Auth Modal */}
					<BasicModal
						width={500}
						showModal={showLogin}
						handleClose={() => setShowLogin(false)}
					>
						<Auth
							setShowLogin={setShowLogin}
							setJustInitSession={setJustInitSession}
							initialMode={authMode}
						/>
					</BasicModal>

					<PurchaseChoiceModal
						open={purchaseChoiceModal.open}
						guide={purchaseChoiceModal.guide}
						onClose={() =>
							setPurchaseChoiceModal({
								open: false,
								guide: null,
								limitInfo: null,
							})
						}
						onBuyGuide={handleConfirmBuyGuide}
						onSubscribe={handleConfirmSubscribe}
						onBuyPack={async (packInfo) => {
							if (!packInfo?.packId) return;
							setPurchaseChoiceModal({
								open: false,
								guide: null,
								limitInfo: null,
							});
							setPurchasingPackId(packInfo.packId);
							try {
								await purchasePack(packInfo.packId);
							} catch (err) {
								enqueueSnackbar(
									err?.message || "No se pudo iniciar la compra del pack",
									{ variant: "error" },
								);
								setPurchasingPackId(null);
							}
						}}
						loading={!!purchasingGuide || !!purchasingPackId}
						limitInfo={purchaseChoiceModal.limitInfo}
					/>

					<ComingSoonModal
						open={comingSoonModal.open}
						onClose={() => setComingSoonModal({ open: false, guideName: null })}
						guideName={comingSoonModal.guideName}
					/>

					<EmailVerificationModal
						open={showEmailVerifyModal}
						onClose={() => setShowEmailVerifyModal(false)}
					/>
				</>
			)}
		</>
	);
};

export default DashboardPage;
